import React from 'react';
import { Conversation } from '@/shared/hooks/useConversations';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { PremiumBadge } from '@/components/ui/PremiumBadge';
import { cn } from '@/lib/utils';
import { getInitials } from '@/utils/avatarUtils';
import { useI18n } from '@/contexts/I18nContext';

interface ConversationItemProps {
  conversation: Conversation;
  onClick: () => void;
}

function formatDate(iso: string | null, t: (key: string) => string): string {
  if (!iso) return '';
  const date    = new Date(iso);
  const now     = new Date();
  const diffMs  = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHrs = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1)  return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  if (diffHrs < 24) return `${diffHrs}h`;
  if (diffDays === 1) return t('pages.messages.yesterday');
  if (diffDays < 7)
    return date.toLocaleDateString([], { weekday: 'short' }); // Mon, Tue…
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' }); // Feb 14
}

export const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  onClick,
}) => {
  const { t } = useI18n();
  const hasUnread = conversation.unread_count > 0;
  const initials  = getInitials(conversation.other_user_name);

  const preview = conversation.last_message_preview
    ? conversation.last_message_is_mine
      ? `${t('pages.messages.you_prefix')} ${conversation.last_message_preview}`
      : conversation.last_message_preview
    : null;

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left"
    >
      {/* Belt avatar */}
      <FramedAvatar
        src={conversation.other_user_picture ?? undefined}
        initials={initials}
        beltRank={conversation.other_user_belt_rank}
        username={conversation.other_user_username ?? undefined}
        frameSize={{ w: 54, h: 60 }}
        isManual
      />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-baseline gap-2">
          <span className="flex items-center gap-1.5 min-w-0">
            <span
              className={cn(
                'text-sm truncate',
                hasUnread ? 'text-white font-semibold' : 'text-white/80',
              )}
            >
              {conversation.other_user_name}
            </span>
            {conversation.other_user_is_premium && <PremiumBadge size="xs" />}
          </span>

          <span
            className={cn(
              'text-[11px] flex-shrink-0 tabular-nums',
              hasUnread ? 'text-[#E05C2A] font-medium' : 'text-white/40',
            )}
          >
            {formatDate(conversation.last_message_at, t)}
          </span>
        </div>

        <div className="flex justify-between items-center mt-0.5 gap-2">
          <p
            className={cn(
              'text-xs truncate',
              hasUnread ? 'text-white/70' : 'text-white/40',
            )}
          >
            {preview ?? t('pages.messages.no_preview')}
          </p>

          {hasUnread && (
            <div className="flex-shrink-0 bg-[#E05C2A] text-white text-[10px] font-bold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center">
              {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
            </div>
          )}
        </div>
      </div>
    </button>
  );
};
